/**
 * The Library's masters badge (1.2.0): "UHD · HD · TXL" on an asset with more than one master (formats, then the codes
 * of variants other than Programme), from AssetSummary.masterSummary. An asset with one Programme master shows nothing
 * (that is every asset from before 1.2.0). Rendered by LibraryItems on cards and table rows.
 */
import type { AssetSummary, MasterSummaryDTO, MasterVariantDTO } from '@prism/contracts';
import { PROGRAMME_VARIANT_KEY } from '@prism/contracts';
import { useMasterVariants } from '../api/hooks';

/** The badge's parts: format classes (largest first), then the codes of the non-Programme variants in the list's order. */
export function masterBadgeParts(summary: MasterSummaryDTO | null | undefined, variants: readonly Pick<MasterVariantDTO, 'id' | 'key' | 'code'>[]): string[] {
  if (!summary) return [];
  const known = new Map(variants.map((v) => [v.id, v]));
  const codes = summary.variantIds
    .map((id) => known.get(id))
    .filter((v): v is Pick<MasterVariantDTO, 'id' | 'key' | 'code'> => Boolean(v) && v!.key !== PROGRAMME_VARIANT_KEY)
    .sort((a, b) => variants.indexOf(a) - variants.indexOf(b))
    .map((v) => v.code);
  if (summary.count <= 1 && !codes.length) return [];
  const formats = summary.formats.filter((c) => c !== 'Audio' || summary.formats.length === 1);
  return [...formats, ...codes];
}

export function MasterBadge({ asset, compact = false }: { asset: AssetSummary; compact?: boolean }) {
  const summary = asset.masterSummary ?? null;
  const variants = useMasterVariants(Boolean(summary)).data?.items ?? [];
  const parts = masterBadgeParts(summary, variants);
  if (!parts.length) return null;
  const count = summary?.count ?? 0;
  return (
    <span className={`pill master-badge${compact ? ' compact' : ''}`} data-testid="master-badge"
      title={`${count} master${count === 1 ? '' : 's'}: ${parts.join(', ')}`}>
      {parts.join(' · ')}
    </span>
  );
}
